import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToEl } from "@/lib/scroll";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1649968399156-47b95b9472d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwcG9ydHJhaXQlMjBzcG90bGlnaHQlMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1Mjc4OTY2fDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onConsult }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} id="hero" className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]" data-testid="hero">
      {/* Parallax athlete image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Athlete" className="h-full w-full object-cover object-[70%_center] opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/20 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 min-h-[100svh] flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="font-mono-accent text-[#005EEA] text-xs mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#005EEA]" /> Personal Training · S&C · Bootcamps
        </motion.p>

        <h1 className="font-display text-white leading-[0.82] tracking-tight text-[16vw] md:text-[13vw] lg:text-[11vw]">
          <Line delay={0.5}>Forge a</Line>
          <Line delay={0.65}>
            body that <span className="text-[#005EEA]">wins.</span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease }}
          className="mt-8 flex flex-col md:flex-row md:items-end gap-8 md:justify-between"
        >
          <p className="max-w-md text-[#A0A0A0] text-base md:text-lg leading-relaxed">
            Coach Jean Claude builds relentless athletes through personalised strength &
            conditioning and high-energy bootcamps. Your transformation starts with one conversation.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <button
              onClick={onConsult}
              className="group font-mono-accent text-xs bg-[#005EEA] text-white px-8 py-5 hover:bg-[#0047b3] transition-colors shadow-[0_0_30px_rgba(0,94,234,0.35)]"
              data-testid="hero-consult-btn"
            >
              Book Free Consult
            </button>
            <button
              onClick={() => scrollToEl("#pricing")}
              className="font-mono-accent text-xs border border-white/25 text-white px-8 py-5 hover:bg-white hover:text-black transition-colors"
              data-testid="hero-pricing-btn"
            >
              View Pricing
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-5 md:right-10 z-10 hidden md:flex items-center gap-2 text-[#666]"
      >
        <span className="font-mono-accent text-[10px]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
